export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface AnchoredOverlayPosition {
  readonly top: number;
  readonly left: number;
  readonly arrowLeft: number;
  readonly placement: 'top' | 'bottom';
}

const EDGE_PADDING = 8;
const ARROW_INSET = 12;

/**
 * Places a floating overlay against its trigger in viewport coordinates, flipping above the
 * trigger when there is not enough room below and keeping the overlay inside the viewport.
 *
 * Viewport coordinates let the overlay be rendered through a portal, so it is never clipped
 * by an ancestor that hides its overflow or applies a transform.
 */
export const computeAnchoredOverlayPosition = (
  trigger: DOMRect,
  overlayWidth: number,
  overlayHeight: number,
  viewport: Viewport,
  margin = 10
): AnchoredOverlayPosition => {
  const spaceBelow = viewport.height - trigger.bottom;
  const spaceAbove = trigger.top;
  const placeAbove = spaceBelow < overlayHeight + margin && spaceAbove > spaceBelow;

  const top = placeAbove ? trigger.top - overlayHeight - margin : trigger.bottom + margin;

  const left = Math.max(
    EDGE_PADDING,
    Math.min(trigger.right - overlayWidth, viewport.width - overlayWidth - EDGE_PADDING)
  );

  const triggerCentre = trigger.left + trigger.width / 2;
  const arrowLeft = Math.max(
    ARROW_INSET,
    Math.min(triggerCentre - left, overlayWidth - ARROW_INSET)
  );

  return { top, left, arrowLeft, placement: placeAbove ? 'top' : 'bottom' };
};
